<template lang="pug">
#get-residents-for-cooperation-system(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 連携システムからの利用者取り込み

    .box-body
      .d-flex.flex-column.mt-1
        span
          span 連携システムから利用者を取得して保険者番号・被保険者番号の一致する利用者をシステムが確認し、
          span.key-color-message.font-weight-bold 新規
          span ・
          span.warning-message.font-weight-bold 削除
          span ・
          span.font-weight-bold 変更
          span の反映ができます。
        span
          span 本機能を使用するためには、HitomeQ コネクトと連携システムの両方で
          span.warning-message 保険者番号と被保険者番号の設定が必要
          span です。
        span
          span.key-color-message.font-weight-bold 新規
          span : 連携システムで追加された利用者を登録、
          span.warning-message.font-weight-bold 削除
          span : 連携システムで削除された利用者を削除、
          span.font-weight-bold 変更
          span : 連携システムで変更された内容を反映
      .d-flex.flex-row.align-items-center.mt-1
        span.use-system-title.mr-2 連携システム
        b-form-select.mr-2(v-model="useSystem" :options="_useSystemOptions" style="max-width:320px;")
        b-button(@click="onGetResidents(useSystem)" :class="{ 'min-width': '90px', 'disable': !useSystem }" :disabled="!useSystem") 取得
      
      .hitomeq-care-support-table.mt-4(v-if="showCsTable && 0 < csItems.length")
        ResidentsForCooperationSystemTable(
          v-model="csItems"
          :fields="csFields"
          :totalRows ="csTotalRows"
          :currentPage="csCurrentPage"
          :perPage="perPage"
          :filter="csFilter"
          :roomOptions="roomOptions"
          :groupOptions="residentGroupOptions"
          :residentGroupMap="residentGroupMap"
          :allSelect.sync="csAllSelects[Number(csCurrentPage) - 1]"
          @onAllSelect="changeCsAllSelect"
          @onDetail="onDetail")
        .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
          .search-filter.d-flex.mr-1.align-items-center.mr-2
            b-input-group.search-filter-input-group.mr-1(size="md")
              b-input-group-prepend
                b-input-group-text.search-filter-icon
                  .search-icon
              b-form-input.search-filter-input(v-model="csFilter.searchWord" type="search" placeholder="検索ワードを入力してください")
            HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="search-filter-help" title="検索項目について")
              template(v-slot:text)
                div 検索項目は以下になります。
                div ・利用者名
                div ・利用者名（ふりがな）
          span.table-option-text 状況:
          .filter
            b-form-select(id="cs-filter-input" v-model="csFilter.type" :options="csFilterOptions" size="md")
          span.table-option-text 1ページの行数:
          .per-page
            b-form-select(id="cs-per-page-select" v-model="perPage" :options="pageOptions" size="md" @change="selectperPage")
          b-pagination.m-0(v-model="csCurrentPage" :total-rows="csTotalRows" :per-page="perPage" :disabled="isBusy"
            @change="selectCsPage" hide-goto-end-buttons)

    .box-footer.with-border.register-footer
      .error-message {{ errorMessage }}
      .buttons
        button.btn.update-btn(@click="onOk" type="button" :class="{ 'disable': !_canOk }" :disabled="!_canOk") チェックした項目を反映
        button.btn.cancel-btn(@click="gotoResidentList" type="button") キャンセル

  ResidentDetailModalForCooperationSystem(v-model="showRegisterAndDeleteDetailModal" :item="itemForDetailModal")
  ResidentDiffDetailModalForCooperationSystem(v-model="showUpdateDetailModal" :item="itemForDetailModal" :useSystem="useSystem")

  //- delete modal
  ResidentCheckDeleteModal(
    v-model="showConfirmModal"
    title="利用者情報をHitomeQコネクトに反映しますか？"
    :sendMessage.sync="sendDeleteMessage" :deleteMessageTitle.sync="deleteModalMessageTitle" :deleteMessageText.sync="deleteModalMessageText"
    :showSendDeleteMessage="showSendDeleteMessage"
    @ok="onConfirmOk")
    template(#message)
      .message.mb-2 内容を確認し問題なければ、「確認しました」にチェックしてOKしてください。
      div(v-if="0 < _deleteTargetItems.length")
        .warning-message.font-weight-bold 削除
        ul.confirm-list
          li(v-for="item in _deleteTargetItems") {{ item.name }}
      div(v-if="0 < _registerTargetItems.length")
        .key-color-message.font-weight-bold 新規
        ul.confirm-list
          li(v-for="item in _registerTargetItems") {{ item.name }}
      div(v-if="0 < _updateTargetItems.length")
        .font-weight-bold 変更
        ul.confirm-list
          li(v-for="item in _updateTargetItems") {{ item.name }}
  OkModal(v-model="showSuccessModal" title="反映に成功しました。" @ok="gotoResidentList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
  ProgressLoading(v-model="currentCount" :max="totalCount" :isBusy="progressLoading" message="反映中...")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { ResidentListState, SendMessagesState } from "@/store/NeedRefreshes";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import ResidentsService, {
  InAndOutDatetime,
  ResidentDeleteModel,
  ResidentGetForHitomeQCareSupportModel,
  ResidentPatchModel,
  ResidentPostModel,
  ResidentWithDiff,
  ResidentWithRoomAndGroup
} from "@/commons/services/Residents";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import RoomsService, { RoomGetModel } from "@/commons/services/Rooms";

import ResidentsForCooperationSystemTable, {
  ResidentsForCooperationSystemTableField,
  ResidentsForCooperationSystemTableFilterSetting,
  ResidentsForCooperationSystemTableItem
} from "@/pc/containers/residents/ResidentsForCooperationSystemTable.vue";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ProgressLoading from "@/pc/basics/ProgressLoading.vue";
import ResidentDetailModalForCooperationSystem from "@/pc/containers/residents/ResidentDetailModalForCooperationSystem.vue";
import ResidentDiffDetailModalForCooperationSystem from "@/pc/containers/residents/ResidentDiffDetailModalForCooperationSystem.vue";
import ResidentCheckDeleteModal from "@/pc/components/residents/ResidentCheckDeleteModal.vue";

/**
 * ### 連携システムからの利用者取り込み
 */
@Component({
  components: {
    ResidentsForCooperationSystemTable,
    ResidentDetailModalForCooperationSystem,
    ResidentDiffDetailModalForCooperationSystem,
    HelpIcon,
    ResidentCheckDeleteModal,
    OkModal,
    PageLoading,
    ProgressLoading
  },
  $_veeValidate: { validator: "new" }
})
export default class GetResidentsForCooperationSystem extends Vue {
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** プログレスローディング表示可否 */
  progressLoading: boolean = false;
  /** プログレスローディング現在値 */
  currentCount: number = 0;
  /** プログレスローディング全体値 */
  totalCount: number = 0;

  /** エラーメッセージ */
  errorMessage: string = "";

  /** 確認モーダル表示可否 */
  showConfirmModal: boolean = false;
  /** 削除メッセージを表示するか否か、家族がいない場合の利用者削除の場合は削除メッセージは送信不要なので表示制御している */
  showSendDeleteMessage: boolean = false;
  /** 削除メッセージの送信可否 */
  sendDeleteMessage: boolean | null = null;
  /** 削除メッセージタイトル */
  deleteModalMessageTitle: string = "";
  /** 削除メッセージ本文 */
  deleteModalMessageText: string = "";
  /** 完了モーダル表示可否 */
  showSuccessModal: boolean = false;

  /** 詳細モーダル（新規登録、削除）表示可否 **/
  showRegisterAndDeleteDetailModal: boolean = false;
  /** 詳細モーダル（更新）表示可否 **/
  showUpdateDetailModal: boolean = false;
  /** 詳細モーダル（共通）表示内容 */
  itemForDetailModal: ResidentsForCooperationSystemTableItem | null = null;

  /** 使用する連携システム */
  useSystem: UseSystem | null = null;

  /**
   * HitomeQケアサポートテーブル情報
   */
  /** 表示可否 */
  showCsTable: boolean = false;
  /** 行 */
  csItems: Array<ResidentsForCooperationSystemTableItem> = [];
  /** テーブルの「選択」列のチェックボックス値 */
  csAllSelects: boolean[] = [];
  /** 列名 */
  csFields: Array<ResidentsForCooperationSystemTableField> = [];
  /** 全体件数 */
  csTotalRows = 0;
  /** 現在ページ */
  csCurrentPage: number = 0;

  /**
   * i-Medic連携テーブル情報
   */
  /** 表示可否 */
  showImTable: boolean = false;
  /** 行 */
  imItems: Array<any> = [];
  /** テーブルの「選択」列のチェックボックス値 */
  imAllSelects: boolean[] = [];
  /** 列名 */
  imFields: Array<any> = [];
  /** 全体件数 */
  imTotalRows = 0;
  /** 現在ページ */
  imCurrentPage: number = 0;

  /**
   * 連携テーブル共通
   */
  /** 1ページあたりの行数 */
  perPage: number = Number(Setting.resident.getResidentsForCooperationSystem.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.resident.getResidentsForCooperationSystem.pageOptions;

  /** フィルタリング */
  csFilter: ResidentsForCooperationSystemTableFilterSetting = {
    type: "全て",
    searchWord: ""
  };
  csFilterOptions: Array<{ value: string; text: string }> = [
    { value: "全て", text: "全て" },
    { value: "新規", text: "新規" },
    { value: "変更", text: "変更" },
    { value: "削除", text: "削除" }
  ];

  /**
   * テーブル共通
   */
  /** 部屋選択肢 */
  roomOptions: Array<{ value: number; text: string }> = [];
  /** 利用者グループ選択肢 */
  residentGroupOptions: Array<{ value: number; text: string }> = [];
  /** 利用者グループMAP（key:グループID、value:利用者グループ情報） */
  residentGroupMap: Map<number, ResidentGroupsGetModel> = new Map();

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  private setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `get-residents-for-cooperation-system-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  @Watch("sendDeleteMessage")
  changeSendDeleteMessage(sendDeleteMessage: boolean): void {
    this.setCookieForSendDeleteMessage(sendDeleteMessage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  private setCookieForSendDeleteMessage(sendDeleteMessage: boolean): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `get-residents-for-cooperation-system-send-delete-message=${sendDeleteMessage}; max-age=${saveSecond};`;
    return;
  }

  /** 使用する連携システム選択肢 */
  get _useSystemOptions(): Array<{ value: string; text: string }> {
    const options = [];
    if (userStore.facility.cooperationSystems.hitomeQCareSupport == true) {
      options.push({ value: UseSystem.CARE_SUPPOURT, text: "HitomeQ ケアサポート" });
    }
    if (userStore.facility.cooperationSystems.iMedic) {
      options.push({ value: UseSystem.I_MEDIC, text: "i-MEDIC Plus 介護" });
    }
    return options;
  }

  /** ### 新規登録対象の行情報を返す */
  get _registerTargetItems(): Array<ResidentWithRoomAndGroup> {
    return this.csItems.filter((item) => {
      return item.type == "新規" && item.checked == true;
    });
  }

  /** ### 更新対象の行情報を返す */
  get _updateTargetItems(): Array<ResidentWithRoomAndGroup> {
    return this.csItems.filter((item: any) => {
      return item.type == "変更" && item.checked == true;
    });
  }

  /** ### 削除対象の行情報を返す */
  get _deleteTargetItems(): Array<ResidentWithRoomAndGroup> {
    return this.csItems.filter((item) => {
      return item.type == "削除" && item.checked == true;
    });
  }

  /** ### 反映ボタン押下可否を返す */
  get _canOk(): boolean {
    switch (this.useSystem) {
      case UseSystem.CARE_SUPPOURT:
      case UseSystem.I_MEDIC: {
        for (const item of this.csItems) {
          if (item.checked == true) {
            return true;
          }
        }
        return false;
      }
    }
    return false;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.resident.getResidentsForCooperationSystem.perPage);
    // 削除時のメッセージ送信可否はCookieから取得する。取得出来ない場合はnull（未入力）を使用する
    const sendDeleteMessage: boolean | null = this.loadCookieForSendDeleteMessage();
    this.sendDeleteMessage = sendDeleteMessage != null ? sendDeleteMessage : null;

    await this.createView();
    return;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["get-residents-for-cooperation-system-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  private loadCookieForSendDeleteMessage(): boolean | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      const sendDeleteMessage: string = cookieMap["get-residents-for-cooperation-system-send-delete-message"];
      if (sendDeleteMessage == "true") {
        return true;
      } else if (sendDeleteMessage == "false") {
        return false;
      } else {
        return null;
      }
    }

    return null;
  }

  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const rooms: Array<RoomGetModel> = await this.getRooms();
    const roomOptions: Array<{ value: number; text: string }> = this.createRoomOptions(rooms);
    this.roomOptions = roomOptions;

    const residentGroups: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    const residentGroupOptions: Array<{ value: number; text: string }> = this.createResidentGroupOptions(residentGroups);
    this.residentGroupOptions = residentGroupOptions;
    const residentGroupMap: Map<number, ResidentGroupsGetModel> = this.createResidentGroupMap(residentGroups);
    this.residentGroupMap = residentGroupMap;

    this.isBusy = false;
    return;
  }

  /**
   * ### フィルタで使用する利用者グループ選択肢を作成する
   */
  public createRoomOptions(rooms: Array<RoomGetModel>): Array<{ value: number; text: string }> {
    const options: Array<{ value: number; text: string }> = [];
    for (const room of rooms) {
      options.push({
        value: room.id,
        text: room.name
      });
    }
    return options;
  }

  /**
   * ### フィルタで使用する利用者グループ選択肢を作成する
   */
  public createResidentGroupOptions(groups: Array<ResidentGroupsGetModel>): Array<{ value: number; text: string }> {
    const options: Array<{ value: number; text: string }> = [];
    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of groups) {
      if (allowedGroupsMap.has(group.id)) {
        options.push({
          value: group.id,
          text: group.name
        });
      }
    }
    return options;
  }

  /**
   * ### 利用者グループMAPを作成する
   */
  public createResidentGroupMap(groups: Array<ResidentGroupsGetModel>): Map<number, ResidentGroupsGetModel> {
    const map: Map<number, ResidentGroupsGetModel> = new Map();
    for (const group of groups) {
      map.set(group.id, group);
    }
    return map;
  }

  /**
   * ### 部屋取得
   */
  public async getRooms(): Promise<Array<RoomGetModel>> {
    const response: Array<RoomGetModel> = await this.roomsService.gets(userStore);
    return response;
  }

  /**
   * ### バリデーションで使用する部屋情報MAPを作成する
   */
  public createRoomMap(rooms: Array<RoomGetModel>): Map<string, RoomGetModel> {
    const map: Map<string, RoomGetModel> = new Map();
    for (const room of rooms) {
      map.set(room.name, room);
    }
    return map;
  }

  /**
   * ### 連携システムから利用者情報を取得して画面表示する
   */
  public async onGetResidents(useSystem: UseSystem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 列名は共通のものを作成する
    const csFields = this.createCsFields();
    this.csFields = csFields;

    // 行情報は連携システム固有の情報を取得し、行情報を作成する
    switch (useSystem) {
      case UseSystem.CARE_SUPPOURT: {
        const residents: ResidentGetForHitomeQCareSupportModel = await this.getResidentsForHitomeQCareSupport();
        const csItems: Array<ResidentsForCooperationSystemTableItem> = this.createCsItems(residents);
        this.csItems = csItems;
        break;
      }
      case UseSystem.I_MEDIC: {
        try {
          const residents: ResidentGetForHitomeQCareSupportModel = await this.getResidentsForIMedic();
          const csItems: Array<ResidentsForCooperationSystemTableItem> = this.createCsItems(residents);
          this.csItems = csItems;
        } catch (error) {
          // residents.csvが置かれていない場合はエラーが返却されるのでチェックする
          const systemError = error as SystemError;
          if (systemError.statusCode == 404 && systemError.originalResponse.message == "residents.csv is not found") {
            this.errorMessage = "アップロードされている利用者一覧のファイルがありません。";
          } else {
            throw error;
          }
        }
        break;
      }
      default: {
        return;
      }
    }

    // テーブルのフィルタリング情報は共通のものを格納する
    this.csTotalRows = this.csItems.length;
    this.csCurrentPage = 1;
    this.perPage = 50;
    this.csFilter = {
      type: "全て",
      searchWord: ""
    };
    this.showCsTable = true;

    this.isBusy = false;
    return;
  }

  /**
   * ### 列名を作成する
   */
  public createCsFields(): Array<ResidentsForCooperationSystemTableField> {
    const fields: Array<ResidentsForCooperationSystemTableField> = [];
    fields.push({ key: "checked", label: "", sortable: false, sortDirection: "desc", class: "checked text-center" });
    fields.push({ key: "type", label: "状況", sortable: false, sortDirection: "desc", class: "type text-center" });
    fields.push({ key: "name", label: "利用者名", sortable: false, sortDirection: "desc", class: "name" });
    fields.push({ key: "roomName", label: "部屋名", sortable: false, sortDirection: "desc", class: "room-name" });
    fields.push({ key: "groupName", label: "利用者グループ", sortable: false, sortDirection: "desc", class: "group-name" });
    fields.push({ key: "detail", label: "登録情報", sortable: false, sortDirection: "desc", class: "detail text-center" });

    // v3.34.0まではケアサポートの部屋名と本アプリの部屋名が一致しなければエラーとし、備考に表示していた
    // v3.35.0からは部屋名が異なっても選択肢で編集可能なので備考が不要になった
    // 今後新しい連携システムでは仕様が変わる可能性があるのでコメントアウトで残す
    // fields.push({ key: "errors", label: "備考", sortable: false, sortDirection: "desc", class: "errors" });

    return fields;
  }

  /**
   * ### HitomeQケアサポートから利用者情報を取得する
   */
  public async getResidentsForHitomeQCareSupport(): Promise<ResidentGetForHitomeQCareSupportModel> {
    const response: ResidentGetForHitomeQCareSupportModel = await this.residentsService.getsForHitomeQCareSupport(userStore);
    // console.log("getResidentsForHitomeQCareSupport() response", response);
    return response;
  }

  /**
   * ### IMedicから利用者情報を取得する
   */
  public async getResidentsForIMedic(): Promise<ResidentGetForHitomeQCareSupportModel> {
    const response: ResidentGetForHitomeQCareSupportModel = await this.residentsService.getsForIMedic(userStore);
    // console.log("getResidentsForIMedic() response", response);
    return response;
  }

  /**
   * ### HitomeQケアサポートのテーブル情報を作成する
   */
  public createCsItems(residents: ResidentGetForHitomeQCareSupportModel): Array<ResidentsForCooperationSystemTableItem> {
    const items: Array<ResidentsForCooperationSystemTableItem> = [];
    for (const resident of residents.residentsForDelete) {
      items.push({
        ...resident,
        checked: false,
        type: "削除",
        errors: [],
        _rowVariant: "",

        // 不要な情報だが型定義のために格納する
        csResidentKanaName: "",
        csResidentName: "",
        csGender: "",
        csBirthday: 0,
        csTel: "",
        csRoomId: null,
        csRoomName: "",
        csInsuredNumber: "",
        csCareInsuranceInsuredNumber: "",
        csInDatetime: null,
        csOutDatetime: null
      });
    }
    for (const resident of residents.residentsForRegistration) {
      items.push({
        ...resident,
        checked: false,
        type: "新規",
        errors: [],
        _rowVariant: ""
      });
    }
    for (const resident of residents.residentsForUpdate) {
      items.push({
        ...resident,
        checked: false,
        type: "変更",
        errors: [],
        _rowVariant: ""
      });
    }
    return items;
  }

  /**
   * ### ケアサポートテーブルの1ページ表示行数の変更
   */
  public async selectperPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.csCurrentPage = 1;
    return;
  }

  /**
   * ### ケアサポートテーブルのページ変更
   */
  public async selectCsPage(page: number): Promise<void> {
    this.csCurrentPage = page;
    return;
  }

  /**
   * ### 全て選択押下イベント
   */
  public changeCsAllSelect(): void {
    const start: number = (Number(this.csCurrentPage) - 1) * Number(this.perPage);
    const end: number = Number(start) + Number(this.perPage);
    for (let i = start; i < end; i++) {
      const allSelects = this.csAllSelects[Number(this.csCurrentPage) - 1];
      if (this.csItems[i] && this.csItems[i].errors.length < 1) {
        this.csItems[i].checked = allSelects;
        this.csItems[i]._rowVariant = allSelects ? "row-checked" : "";
      }
    }
    this.$forceUpdate(); // 再描画して背景色を再描画する
    return;
  }

  /**
   * ### 詳細モーダルを表示する
   */
  public onDetail(item: ResidentsForCooperationSystemTableItem): void {
    switch (item.type) {
      case "新規":
      case "削除": {
        this.itemForDetailModal = item;
        this.showRegisterAndDeleteDetailModal = true;
        break;
      }
      case "変更": {
        this.itemForDetailModal = item;
        this.showUpdateDetailModal = true;
        break;
      }
    }
    return;
  }

  /**
   * ### 確認モーダルを表示する
   */
  public async onOk(): Promise<void> {
    // バリデーション対象を絞り込む
    const targetItems: Array<ResidentsForCooperationSystemTableItem> = this.csItems.filter((item) => {
      return item.errors.length < 1 && item.checked == true;
    });
    let validationResult: boolean = true;
    for (const item of targetItems) {
      this.$validator.errors.clear();

      switch (item.type) {
        case "新規":
        case "変更": {
          // 部屋名バリデーション
          if (!item.csRoomId) {
            this.$validator.errors.add({
              field: "room-" + item.insuredNumber + "-" + item.careInsuranceInsuredNumber,
              msg: "部屋名は必須項目です"
            });
            validationResult = false;
          }
          // 利用者グループバリデーション
          if (!item.groupId) {
            this.$validator.errors.add({
              field: "group-" + item.insuredNumber + "-" + item.careInsuranceInsuredNumber,
              msg: "利用者グループは必須項目です"
            });
            validationResult = false;
          }
          break;
        }
        default: {
          continue;
        }
      }
    }
    // バリデーションエラーの場合は終了
    if (!validationResult) {
      return;
    }

    // 削除対象の利用者に家族が存在する場合はメッセージを入力させる
    this.showSendDeleteMessage = false;
    for (const target of this._deleteTargetItems) {
      if (target.familyCount && 0 < target.familyCount) {
        this.showSendDeleteMessage = true;
      }
    }

    // 家族が登録されている場合は削除メッセージの初期値を入力する
    if (this.showSendDeleteMessage == true) {
      this.deleteModalMessageTitle = "ユーザー登録が削除されました";
      const deleteMessages = [];
      deleteMessages.push(`ご利用の施設から登録情報が削除されましたので、ユーザー登録を削除させていただきました。`);
      deleteMessages.push("今までHitomeQコネクトをご愛顧いただきありがとうございました。");
      this.deleteModalMessageText = deleteMessages.join("\n");
    } else {
      this.deleteModalMessageTitle = "";
      this.deleteModalMessageText = "";
    }

    this.showConfirmModal = true;
    return;
  }

  /**
   * ### 選択された利用者を登録・更新・削除する
   *
   * - APIリクエスト数でインジケータを表示する
   */
  public async onConfirmOk(): Promise<void> {
    this.progressLoading = true;

    try {
      // インジケータを表示するために対象のitemに絞りこむ
      const targetItems: Array<ResidentsForCooperationSystemTableItem> = this.csItems.filter((item) => {
        return item.errors.length < 1 && item.checked == true;
      });

      this.currentCount = 0;
      this.totalCount = targetItems.length;

      for (const item of targetItems) {
        switch (item.type) {
          case "新規": {
            const requestBody: ResidentPostModel = this.createPostRequestBody(item);
            await this.residentsService.post(userStore, requestBody);
            break;
          }
          case "変更": {
            const requestBody: ResidentPatchModel = this.createPatchRequestBody(item);
            await this.residentsService.patch(userStore, item.id as number, requestBody);
            break;
          }
          case "削除": {
            const requestBody: ResidentDeleteModel = {
              deleteMessageTitle: this.sendDeleteMessage ? this.deleteModalMessageTitle : "",
              deleteMessageText: this.sendDeleteMessage ? this.deleteModalMessageText : ""
            };
            await this.residentsService.delete(userStore, item.id as number, requestBody);
            break;
          }
        }
        this.currentCount++;
      }
      this.showSuccessModal = true;
    } catch (error) {
      // 利用者数の上限に達している場合、ステータスコード422が返却される
      // その場合はエラーメッセージを表示する
      // ステータスコード422以外は例外を投げなおして共通エラーハンドラに処理を委譲する
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 422) {
        this.errorMessage = "ご契約プランの利用者登録数上限に達しています。\n新規に追加する場合は設定画面からプラン変更もしくは利用者を削除してから行ってください。";
      }
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      else if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      // 1件以上APIリクエストが成功していればstore更新する
      if (0 < this.currentCount) {
        // 利用者一覧を再読み込みさせるようstoreを更新
        let residentList: ResidentListState = needRefreshesStore.residentList;
        residentList.residentList = true;
        needRefreshesStore.setResidentList(residentList);
        // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
        let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
        sendMessageState.sendMessages = true;
        needRefreshesStore.setSendMessage(sendMessageState);
      }

      this.progressLoading = false;
    }

    return;
  }

  public createPostRequestBody(item: ResidentWithDiff): ResidentPostModel {
    // inAndOutDatetimesが存在する場合、部屋IDを付与する
    if (0 < item.inAndOutDatetimes.length) {
      for (const inAndOutDatetime of item.inAndOutDatetimes) {
        inAndOutDatetime.roomId = item.csRoomId as number;
      }
    }

    return {
      kanaName: item.kanaName ? item.kanaName : "",
      name: item.name,
      gender: item.gender,
      birthday: item.birthday ? item.birthday : null, // 必須入力ではないものはnullがありえるのでチェックしてnull格納
      tel: item.tel ? item.tel : "", // 必須入力ではないものはnullがありえるので空文字に置き換える
      groupId: item.groupId as number,
      roomId: item.csRoomId as number,
      insuredNumber: item.insuredNumber ? item.insuredNumber : "",
      careInsuranceInsuredNumber: item.careInsuranceInsuredNumber ? item.careInsuranceInsuredNumber : "",
      inAndOutDatetimes: item.inAndOutDatetimes ? item.inAndOutDatetimes : []
    };
  }

  public createPatchRequestBody(item: ResidentWithDiff): ResidentPatchModel {
    // iMedicの場合、入所退所の更新が必要
    // inAndOutDatetimesが存在する場合、部屋IDを付与する
    let inAndOutDatetimes = item.inAndOutDatetimes;
    switch (this.useSystem) {
      case UseSystem.I_MEDIC: {
        inAndOutDatetimes = this.mergeInAndOutDatetimeForIMedic(item);
        break;
      }
    }

    return {
      kanaName: item.csResidentKanaName ? item.csResidentKanaName : "",
      name: item.csResidentName,
      gender: item.csGender,
      birthday: item.csBirthday ? item.csBirthday : null, // 必須入力ではないものはnullがありえるのでチェックしてnull格納
      tel: item.csTel ? item.csTel : "", // 必須入力ではないものはnullがありえるので空文字に置き換える
      groupId: item.groupId as number,
      roomId: item.csRoomId as number,
      insuredNumber: item.insuredNumber ? item.insuredNumber : "",
      careInsuranceInsuredNumber: item.careInsuranceInsuredNumber ? item.careInsuranceInsuredNumber : "",
      inAndOutDatetimes: inAndOutDatetimes,
      deleteFlag: false,
      updated: item.updated as number
    };
  }

  /**
   * ### HitomeQのデータにi-Medicのデータを取り込むような差分表示データを作成する
   */
  private mergeInAndOutDatetimeForIMedic(item: ResidentWithDiff): Array<InAndOutDatetime> {
    const dataA: Array<InAndOutDatetime> = item.inAndOutDatetimes ? item.inAndOutDatetimes : [];
    const dataB: Array<InAndOutDatetime> = [];
    if (item.csInDatetime || item.csOutDatetime) {
      dataB.push({
        inDatetime: item.csInDatetime,
        outDatetime: item.csOutDatetime,
        pickUpDatetime: null,
        dropOffDatetime: null,
        roomId: item.csRoomId as number
      });
    }

    // データBにinAndOutDatetimesが存在しない場合は、何もせずにdataAを返す
    if (!dataB || dataB.length === 0) {
      return dataA;
    }

    // データBのinAndOutDatetimesは1つまたは0個という前提
    const newEntry = dataB[0];

    // データAのinAndOutDatetimesをループし、inDatetimeとoutDatetimeが一致するものを探す
    const isMatchFound = dataA.some((entry) => {
      // データAは時刻が入力されているので、日付だけにして比較する必要がある
      let _inDatetimeFromDateA = null;
      if (entry.inDatetime) {
        _inDatetimeFromDateA = Number(this.$dateUtil.convertNumberToString(entry.inDatetime, "yyyymmdd"));
      }
      let _outDatetimeFromDateA = null;
      if (entry.outDatetime) {
        _outDatetimeFromDateA = Number(this.$dateUtil.convertNumberToString(entry.outDatetime, "yyyymmdd"));
      }

      // データB日付だけにする
      let _inDatetimeFromDateB = null;
      if (newEntry.inDatetime) {
        _inDatetimeFromDateB = Number(this.$dateUtil.convertNumberToString(newEntry.inDatetime, "yyyymmdd"));
      }
      let _outDatetimeFromDateB = null;
      if (newEntry.outDatetime) {
        _outDatetimeFromDateB = Number(this.$dateUtil.convertNumberToString(newEntry.outDatetime, "yyyymmdd"));
      }

      return _inDatetimeFromDateA === _inDatetimeFromDateB && _outDatetimeFromDateA === _outDatetimeFromDateB;
    });

    // 一致するデータが見つかった場合、取り込まないでそのままdataAを返す
    if (isMatchFound) {
      return dataA;
    }

    // 一致しない場合、データAの最後の要素を新しいデータで上書きする
    // データAの配列が空の場合は、新しい要素を追加する
    if (dataA.length === 0) {
      dataA.push(newEntry);
    } else {
      // 最後の要素のインデックス
      const lastIndex = dataA.length - 1;

      // 最後の要素を新しいデータで上書き
      dataA[lastIndex] = {
        ...dataA[lastIndex], // 既存のプロパティを保持
        inDatetime: newEntry.inDatetime,
        outDatetime: newEntry.outDatetime,
        roomId: newEntry.roomId
      };
    }

    return dataA;
  }

  /**
   * ### 利用者一覧へ遷移する
   */
  public gotoResidentList(): void {
    this.$router.push(PcUrl.ResidentList);
    return;
  }
}

export enum UseSystem {
  NONE = "",
  CARE_SUPPOURT = "careSuport",
  I_MEDIC = "iMedic"
}
</script>

<style lang="scss" scoped>
#get-residents-for-cooperation-system {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .use-system-title {
    font-weight: bold;
    font-size: 18px;
  }
}
</style>
